import React, { useState } from "react";
import {
  safeJSONParse,
  snakeToTitleCase,
  socialMediaScoreParsing,
  socialMediaTotalScoreParsing,
} from "../../../utils/helper";
import ScoreBadge from "../../ScoreBadge";
import styles from "../agent-response.module.scss";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

type SocialMediaInsightsAgentProps = {
  parsedContent: any;
};

export default function SocialMediaInsightsAgent({
  parsedContent,
}: SocialMediaInsightsAgentProps): JSX.Element {
  const [postTable1, setPostTable1] = useState(false);
  const [postTable2, setPostTable2] = useState(false);
  const [postTable3, setPostTable3] = useState(false);

  // Extract all data from parsedContent
  const twitterAnswer = safeJSONParse(parsedContent?.twitterAnswer);
  const linkedinAnswer = safeJSONParse(parsedContent?.linkedinAnswer);
  const instagramAnswer = safeJSONParse(parsedContent?.instagramAnswer);
  const finalAnswer = safeJSONParse(parsedContent?.finalAnswer);
  const images = safeJSONParse(parsedContent?.images);
  const socialMediaLocalizer = safeJSONParse(
    parsedContent?.socialMediaLocalizer
  );
  const twitterLocalizer = safeJSONParse(socialMediaLocalizer?.rawTwitterLoc);
  const linkedinLocalizer = safeJSONParse(socialMediaLocalizer?.rawLinkedinLoc);
  const instagramLocalizer = safeJSONParse(
    socialMediaLocalizer?.rawInstagramLoc
  );
  const socialMediaImages = safeJSONParse(parsedContent?.socialMediaImages);
  const question = parsedContent?.question;

  const twitterScore = finalAnswer?.payload?.platforms?.filter(
    (platform: any) => platform.platform_id === "x"
  )[0];
  const twitterScoreKeys = twitterScore?.posts?.[0]
    ? Object.keys(twitterScore.posts[0].scores)
    : [];
  const linkedinScore = finalAnswer?.payload?.platforms?.filter(
    (platform: any) => platform.platform_id === "linkedin"
  )[0];

  const linkedinScoreKeys = linkedinScore?.posts?.[0]
    ? Object.keys(linkedinScore.posts[0].scores)
    : [];
  const instagramScore = finalAnswer?.payload?.platforms?.filter(
    (platform: any) => platform.platform_id === "instagram"
  )[0];

  const instagramScoreKeys = instagramScore?.posts?.[0]
    ? Object.keys(instagramScore.posts[0].scores)
    : [];

  // Check if each platform has data
  const hasTwitterData = twitterAnswer?.payload?.posts && twitterAnswer.payload.posts.length > 0;
  const hasLinkedinData = linkedinAnswer?.payload?.posts && linkedinAnswer.payload.posts.length > 0;
  const hasInstagramData = instagramAnswer?.payload?.posts && instagramAnswer.payload.posts.length > 0;

  return (
    <div className={styles.socialMediaContainer}>
      {hasTwitterData && (
      <div>
        <div className={styles.meadiaHeading}>X</div>
        <div className={styles.scoreOptionTable}>
          <div className={styles.optionScroeList}>
            {twitterAnswer?.payload?.posts?.map((post: any, idx: number) => (
              <div key={idx}>
                <h4>
                  X Post&nbsp;{post?.option_number}:&nbsp;
                  <span
                    style={{
                      fontWeight: "600",
                      marginTop: 15,
                    }}
                  >
                    {post?.focus_area || ""}
                  </span>
                </h4>

                <p>{post?.text}</p>
                <div>{post?.read_more}</div>
                <div>{post?.hashtags?.join(" ")}</div>
                {/* localizer content for twitter */}
                <div className="rtl-text localisationTxt">
                  {twitterLocalizer?.payload?.platforms[0]?.posts[idx]?.text}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className={styles.customImg}>
            {images?.rawTwitterImages?.blob_url && (
              <img
                src={images?.rawTwitterImages?.blob_url}
                alt="Preview"
                style={{
                  width: "100%",
                  borderRadius: "8px",
                }}
              />
            )}
          </div>
          <div className={styles.formWrapper}>
            <h2
              className={styles.cardTitle}
              onClick={() => setPostTable1(!postTable1)}
            >
              {postTable1 ? <ExpandLessIcon /> : <ExpandMoreIcon />}X Post
              Assessment
            </h2>
            {postTable1 && (
              <div className={styles.tableWrapper}>
                <div className={styles.scoreLegend}>
                  <div className={styles.legendItem}>
                    <ScoreBadge level="High" showText={false} size={20} />
                    <span>High</span>
                  </div>
                  <div className={styles.legendItem}>
                    <ScoreBadge level="Medium" showText={false} size={20} />
                    <span>Medium</span>
                  </div>
                  <div className={styles.legendItem}>
                    <ScoreBadge level="Low" showText={false} size={20} />
                    <span>Low</span>
                  </div>
                </div>
                <Table size="small" className={styles.fixedTable}>
                  <TableHead>
                    <TableRow>
                      <TableCell className={styles.tableHead}>
                        Criterion
                      </TableCell>
                      {twitterAnswer?.payload?.posts?.map((post: any, idx: number) => (
                        <TableCell key={idx} className={styles.tableHead} align="center">
                          Post {idx + 1}:{" "}
                          {post?.focus_area || ""}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {twitterScoreKeys.map((criterion) => (
                      <TableRow key={criterion}>
                        <TableCell align="left">{snakeToTitleCase(criterion)}</TableCell>
                        {twitterScore.posts.map((post: any, index: number) => {
                          const scoreLevel =
                            criterion === "final_score"
                              ? socialMediaTotalScoreParsing(
                                  post.scores[criterion]
                                )
                              : socialMediaScoreParsing(post.scores[criterion]);
                          return (
                            <TableCell key={index} align="center">
                              <ScoreBadge level={scoreLevel} showText={false} />
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>
      </div>
      )}
      {hasLinkedinData && (
      <div>
        <div className={styles.meadiaHeading}>LinkedIn</div>
        <div className={styles.scoreOptionTable}>
          <div className={styles.optionScroeList}>
            {linkedinAnswer?.payload?.posts?.map((post: any, idx: number) => (
              <div key={idx}>
                <h4>
                  LinkedIn Post {post?.option_number}:&nbsp;
                  <span
                    style={{
                      fontWeight: "600",
                      marginTop: 15,
                      fontSize: 16,
                    }}
                  ></span>
                  {post?.focus_area || ""}
                </h4>

                <p>{post?.text}</p>
                <div>{post?.read_more}</div>
                <div>{post?.hashtags?.join("")}</div>
                {/* localizer content for linkedin */}
                <div className="rtl-text">
                  {linkedinLocalizer?.payload?.platforms[0]?.posts[idx]?.text}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className={styles.customImg}>
          {images?.rawLinkedinImages?.blob_url && (
            <img
              src={images?.rawLinkedinImages?.blob_url}
              alt="Preview"
              style={{
                width: "100%",
                height: "auto",
                borderRadius: "8px",
              }}
            />
          )}
        </div>
        <div className={styles.formWrapper}>
          <h2
            className={styles.cardTitle}
            onClick={() => setPostTable2(!postTable2)}
          >
            {postTable2 ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            LinkedIn Post Assessment
          </h2>
          {postTable2 && (
            <div className={styles.tableWrapper}>
              <div className={styles.scoreLegend}>
                <div className={styles.legendItem}>
                  <ScoreBadge level="High" showText={false} size={20} />
                  <span>High</span>
                </div>
                <div className={styles.legendItem}>
                  <ScoreBadge level="Medium" showText={false} size={20} />
                  <span>Medium</span>
                </div>
                <div className={styles.legendItem}>
                  <ScoreBadge level="Low" showText={false} size={20} />
                  <span>Low</span>
                </div>
              </div>
              <Table size="small" className={styles.fixedTable}>
                <TableHead>
                  <TableRow>
                    <TableCell className={styles.tableHead}>
                      Criterion
                    </TableCell>
                    {linkedinAnswer?.payload?.posts?.map((post: any, idx: number) => (
                      <TableCell key={idx} className={styles.tableHead} align="center">
                        Post {idx + 1}:{" "}
                        {post?.focus_area || ""}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {linkedinScoreKeys.map((criterion: string) => (
                    <TableRow key={criterion}>
                      <TableCell align="left">{snakeToTitleCase(criterion)}</TableCell>

                      {/* Loop through each post option dynamically */}
                      {linkedinScore.posts.map((post: any, index: number) => {
                        const scoreLevel =
                          criterion === "final_score"
                            ? socialMediaTotalScoreParsing(
                                post.scores[criterion]
                              )
                            : socialMediaScoreParsing(post.scores[criterion]);
                        return (
                          <TableCell key={index} align="center">
                            <ScoreBadge level={scoreLevel} showText={false} />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
      )}
      {hasInstagramData && (
      <div>
        <div className={styles.meadiaHeading}>Instagram</div>
        <div className={styles.scoreOptionTable}>
          <div className={styles.optionScroeList}>
            {instagramAnswer?.payload?.posts?.map((post: any, idx: number) => (
              <div key={idx}>
                <h4>
                  Instagram Post {post?.option_number}:&nbsp;
                  <span
                    style={{
                      fontWeight: "600",
                      marginTop: "15px",
                    }}
                  >
                    {post?.focus_area || ""}
                  </span>
                </h4>

                <p>{post?.text}</p>
                <div>{post?.read_more}</div>
                <div>{post?.hashtags?.join("")}</div>
                {/* localizer content for instagram */}
                <div className="rtl-text">
                  {instagramLocalizer?.payload?.platforms[0]?.posts[idx]?.text}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className={styles.customImg}>
          {images?.rawInstagramImages?.blob_url && (
            <img
              src={images?.rawInstagramImages?.blob_url}
              alt="Preview"
              style={{
                width: "100%",
                height: "auto",
                borderRadius: "8px",
              }}
            />
          )}
        </div>
        <div className={styles.formWrapper}>
          <h2
            className={styles.cardTitle}
            onClick={() => setPostTable3(!postTable3)}
          >
            {postTable3 ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            Instagram Post Assessment
          </h2>
          {postTable3 && (
            <div className={styles.tableWrapper}>
              <div className={styles.scoreLegend}>
                <div className={styles.legendItem}>
                  <ScoreBadge level="High" showText={false} size={20} />
                  <span>High</span>
                </div>
                <div className={styles.legendItem}>
                  <ScoreBadge level="Medium" showText={false} size={20} />
                  <span>Medium</span>
                </div>
                <div className={styles.legendItem}>
                  <ScoreBadge level="Low" showText={false} size={20} />
                  <span>Low</span>
                </div>
              </div>
              <Table size="small" className={styles.fixedTable}>
                <TableHead>
                  <TableRow>
                    <TableCell className={styles.tableHead}>
                      Criterion
                    </TableCell>
                    {instagramAnswer?.payload?.posts?.map((post: any, idx: number) => (
                      <TableCell key={idx} className={styles.tableHead} align="center">
                        Post {idx + 1}:{" "}
                        {post?.focus_area || ""}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {instagramScoreKeys.map((criterion: string) => (
                    <TableRow key={criterion}>
                      <TableCell align="left">{snakeToTitleCase(criterion)}</TableCell>

                      {/* Loop through each post option dynamically */}
                      {instagramScore.posts.map((post: any, index: number) => {
                        const scoreLevel =
                          criterion === "final_score"
                            ? socialMediaTotalScoreParsing(
                                post.scores[criterion]
                              )
                            : socialMediaScoreParsing(post.scores[criterion]);
                        return (
                          <TableCell key={index} align="center">
                            <ScoreBadge
                              level={scoreLevel}
                              showText={false}
                              size={20}
                            />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
      )}

      {socialMediaImages &&
        Array.isArray(socialMediaImages) &&
        socialMediaImages.length > 0 && (
          <div className={styles.imageGrid}>
            {socialMediaImages.map((imageItem: any, idx: number) => {
              const blobUrl = imageItem?.result?.blob_url;
              if (!blobUrl) return null;
              return (
                <img
                  key={idx}
                  src={`${blobUrl}?${import.meta.env.VITE_TEMPLATE_BLOB_SAS_TOKEN}`}
                  alt={`Generated image ${idx + 1}`}
                />
              );
            })}
          </div>
        )}

      <div className={styles.finalQuestion}>{question}</div>
    </div>
  );
}
